import Grid from '@mui/material/Grid'
import Icon from 'src/@core/components/icon'
import Image from 'next/image'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import { useCallback, useState, useEffect } from 'react'
import { useDeviceScopedInit } from 'src/hooks/useDeviceScopedInit'

import { useTranslation } from 'src/utils/i18n'

import dayjs from 'dayjs'

import { getGcloudAll } from 'src/@api/dashboard'
import { useDispatch } from 'react-redux'
import { setCurrentTab } from 'src/store/apps/Common'
import ContentLoading from 'src/components/ContentLoading'

const Notes = () => {
  const { t } = useTranslation('common')

  const [initNotes, setInitNotes] = useState([])
  const [notes, setNotes] = useState([])
  const [active, setActive] = useState(0)
  const [selectData, setSelectData] = useState({})
  const [page, setPage] = useState(0)
  const [isLoading, setIsLoading] = useState(true)

  const dispatch = useDispatch()

  // 按当前设备拉 Keep 列表；切换设备时由 useDeviceScopedInit 重跑
  const loadKeep = useCallback(async (_, { signal, isCurrent } = {}) => {
    setIsLoading(true)
    setInitNotes([])
    setNotes([])
    setSelectData({})
    setActive(0)
    setPage(0)

    const username = JSON.parse(localStorage.getItem('DeviceUser') || '{}')?.username || ''

    try {
      const response = await getGcloudAll(
        {
          username,
          params: {
            category: 'KEEP',
            offset: 0,
            limit: 10000
          }
        },
        signal ? { signal } : undefined
      )

      if (isCurrent && !isCurrent()) return
      if (response?.data?.contents == null) {
        setIsLoading(false)

        return
      }

      const result = response.data.contents.map((item, index) => ({
        ...item,
        time: dayjs(item.created).format('D MMM,hh:mm a'),
        id: index
      }))

      setNotes(result)
      setInitNotes(result)
      setSelectData(result[0] || {})
      setIsLoading(false)
    } catch (err) {
      if (isCurrent && !isCurrent()) return
      if (err?.code === 'ERR_CANCELED') return
      console.error('Keep load error:', err)
      setIsLoading(false)
    }
  }, [])

  // 设备切换时重置并重新拉取 Keep
  useDeviceScopedInit(loadKeep)

  const handleFilter = e => {
    let value = e.target.value
    if (value === '') {
      setNotes(initNotes)

      return
    }

    let filterData = initNotes.filter(item => {
      return item.title.toLowerCase().indexOf(value.toLowerCase()) !== -1
    })

    setNotes(filterData)
  }

  const handleSelectClick = id => {
    setActive(id)
    setPage(1)
    let data = notes.find((item, index) => {
      return index === id
    })

    setSelectData(data)
  }

  useEffect(() => {
    const title = t('Keep')
    dispatch(setCurrentTab(title))

    return () => dispatch(setCurrentTab(''))
  }, [dispatch, t])

  if (isLoading) {
    return (
      <div className='relative bg-[#FFF] drop-shadow-[0_0_12px_rgba(0,0,0,0.13)] rounded-[10px] Bxl:h-[calc(var(--vh2)*100-90px)] Bxls:Headers5:h-[calc(var(--vh2)*100-145px)] Header5:big:h-[calc(var(--vh2)*100-210px)] bigs:h-[calc(var(--vh2)*100-270px)] w-[100%] flex'>
        <ContentLoading />
      </div>
    )
  }

  return (
    <>
      <div className=' Bxl:h-[calc(var(--vh2)*100-90px)] Bxls:Headers5:h-[calc(var(--vh2)*100-145px)] Header5:big:h-[calc(var(--vh2)*100-210px)] bigs:h-[calc(var(--vh2)*100-270px)]'>
        <Grid
          item
          container
          spacing={0}
          sx={{ background: 'white', borderRadius: '10px' }}
          style={{ boxShadow: '0px 0px 12px 0px #00000021' }}
          className='h-full'
        >
          <div
            className={`px-[15px] w-[345px] sm:w-full border-r-[1px] border-[rgb(229, 231, 235)] overflow-auto ${page === 1 ? 'sm:hidden' : 'block'
              }`}
          >
            <div className='mt-4'>
              <TextField
                fullWidth
                size='small'
                onInput={e => {
                  handleFilter(e)
                }}
                placeholder={t('Search Notes')}
                sx={{ '& .MuiInputBase-root': { borderRadius: 1 }, paddingLeft: '0px' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Icon icon='mdi:magnify' fontSize='1.25rem' />
                    </InputAdornment>
                  )
                }}
              />
            </div>

            <div className='my-4'>
              <ul className='overflow-y-auto'>
                {notes.map((row, index) => (
                  <li
                    key={row.id}
                    className={`px-[10px] py-[14px] mb-[15px] rounded-[10px] hover:cursor-pointer ${active === index ? 'bg-[#E9E9EB]' : ''
                      }`}
                    style={{ userSelect: 'none' }}
                    onClick={() => handleSelectClick(index)}
                  >
                    <h3 className='text-[14px] text-[#000000]'>{row.title}</h3>
                    <span className='text-[12px] text-[#5a5a5a]'>{row.time}</span>
                  </li>
                ))}
              </ul>
            </div>
            {(initNotes.length > 0 || isLoading) ||
              <div className='text-center px-2 absolute inset-0 flex justify-center items-center pointer-events-none'>
                {t('It appears theres no data to show here yet')}<br />
                {t('If data exists it may take up to 1 hour to download and appear')}<br />
                {t('We suggest checking back in a little while')}
              </div>}
          </div>

          <div className={`w-full flex-1 ${page === 1 ? 'block' : 'sm:hidden'}`}>
            <div className='h-[60px] px-[15px] flex items-center border-b-[1px] border-[#DADADA]'>
              <button onClick={() => setPage(0)} className='sm:block hidden'>
                <Image src='/images/common/left.svg' width={20} height={20} alt='left'></Image>
              </button>
            </div>

            <div className='mx-4 mt-5'>
              <h1 className='sm:text-[14px] text-[#000000] opacity-80 sm:font-bold mb-5'>{selectData.title}</h1>
              <div className='sm:text-[14px] text-[#000000] bg-[#F3F4F6] rounded-[3px] py-[15px] px-[10px]'>
                {selectData.text}
              </div>
            </div>
          </div>
        </Grid>
      </div>
    </>
  )
}

export default Notes
